<script setup lang="ts">
/**
 * Your conversations, one per member, as the portal's chat lists them: the member's avatar, name and role,
 * the last thing said, and how many replies are unread. The open one carries the founder's dirty-white edge.
 * Layout (where the list sits, when it hides) is the caller's.
 *
 * `selectable` makes each row a button that emits `select` with its item (the portal); without it the rows
 * are a picture (the site). The `avatar` slot, scoped by the item, takes a member picture that previews.
 */
import { type ChatListItem, initials } from '../lib/surfaces'

defineProps<{ items: readonly ChatListItem[]; label?: string; selectable?: boolean }>()
const emit = defineEmits<{ select: [item: ChatListItem] }>()
</script>

<template>
    <ul class="zh-chat-list" :aria-label="label ?? 'Your people'">
        <li v-for="item in items" :key="item.id ?? item.member.name">
            <component
                :is="selectable ? 'button' : 'div'"
                :type="selectable ? 'button' : undefined"
                class="zh-chat-list__item"
                :data-open="item.open ? 'true' : undefined"
                :aria-current="selectable && item.open ? 'true' : undefined"
                @click="selectable && emit('select', item)"
            >
                <span class="zh-chat-list__avatar" aria-hidden="true">
                    <slot name="avatar" :item="item">{{ initials(item.member.name) }}</slot>
                </span>
                <span class="zh-chat-list__text">
                    <span class="zh-chat-list__name"
                        >{{ item.member.name }} <span class="zh-chat-list__role">{{ item.role }}</span></span
                    >
                    <span class="zh-chat-list__last">{{ item.last }}</span>
                </span>
                <span v-if="item.unread" class="zh-chat-list__unread">{{ item.unread }} Unread</span>
            </component>
        </li>
    </ul>
</template>

<style scoped>
.zh-chat-list {
    display: grid;
    align-content: start;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

.zh-chat-list__item {
    display: flex;
    box-sizing: border-box;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem;
    border: 0;
    border-left: 2px solid transparent;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
}

button.zh-chat-list__item {
    cursor: pointer;
}

button.zh-chat-list__item:hover {
    background: var(--panel);
}

button.zh-chat-list__item:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: -2px;
}

.zh-chat-list__item[data-open] {
    border-left-color: var(--dirty-white);
    background: var(--panel-2);
}

.zh-chat-list__avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 2.25rem;
    height: 2.25rem;
    overflow: hidden;
    border: 1.5px solid var(--line-strong);
    border-radius: 50%;
    background: var(--void-black);
    color: var(--ink-1);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
}

.zh-chat-list__text {
    display: grid;
    flex: 1;
    min-width: 0;
}

.zh-chat-list__name {
    color: var(--ink-1);
    font-weight: 600;
}

/* The system label (BG §19.3). */
.zh-chat-list__role,
.zh-chat-list__unread {
    margin: 0 0 0 0.375rem;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-chat-list__last {
    overflow: hidden;
    color: var(--ink-3);
    font-size: 0.875rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Unread: a count, a word and a dot, in the command colour, at the row's end. */
.zh-chat-list__unread {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 0.375rem;
    color: var(--electric-cyan);
    font-size: 0.625rem;
}

.zh-chat-list__unread::before {
    content: "";
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: currentColor;
}
</style>
